import { ListTree } from 'lucide-react'
import { DynamicIcon, iconNames } from 'lucide-react/dynamic'
import { DEFAULT_LIST_ICON } from '../lib/lists.js'

/**
 * A Lucide icon by NAME — 'hard-hat', 'list-tree' — for the glyphs a person picks rather
 * than the ones this code picks.
 *
 * Everywhere else the app imports each icon it draws (Icon.jsx, lib/tileLook.js), which is
 * right when the set is known at build time: the bundle carries a few dozen glyphs and
 * nothing else. A list's glyph is any of the library's eighteen hundred, chosen at runtime,
 * and importing them all would put most of a megabyte in front of every page load for a
 * picture on a settings page.
 *
 * lucide-react/dynamic is the library's own answer: each icon is its own module, fetched
 * the first time its name is asked for and cached after. The build therefore emits one
 * small chunk per icon rather than one large one — many files under dist/, each loaded only
 * when a list actually wears it. That trade was taken deliberately; a curated subset was the
 * alternative, and "pick any Lucide icon" was the point.
 *
 * A name the library does not know draws the product's own glyph for a list, so a row whose
 * icon is retired by some future version of the library still has a picture.
 */
const KNOWN = new Set(iconNames)

export const isLucideName = (name) => KNOWN.has(name)

/** Every name, for the picker. Kebab-case, the way the library keys them. */
export const LUCIDE_NAMES = iconNames

// While the module is on its way the space it will take is held, so a row of tiles does not
// shuffle as its glyphs arrive one by one. One placeholder component per size, because
// DynamicIcon mounts whatever it is handed as a component and a fresh closure per render
// would be a fresh component each time.
const SLOTS = new Map()
function slotFor(size) {
  if (!SLOTS.has(size)) {
    const Slot = () => (
      <span className="lucide-slot" style={{ width: size, height: size }} aria-hidden="true" />
    )
    SLOTS.set(size, Slot)
  }
  return SLOTS.get(size)
}

export default function LucideIcon({ name, size = 16, strokeWidth = 1.9, className = '', ...rest }) {
  if (!isLucideName(name)) {
    return (
      <ListTree size={size} strokeWidth={strokeWidth} className={className} aria-hidden="true" {...rest} />
    )
  }
  return (
    <DynamicIcon
      name={name}
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      fallback={slotFor(size)}
      aria-hidden="true"
      {...rest}
    />
  )
}

/**
 * GlyphTile and the settings sidebar take an icon COMPONENT, so a list's glyph has to be
 * one. The same component for the same name every time — a fresh closure per render is a
 * new type to React, which tears the tile down and shows the loading slot again.
 */
const GLYPHS = new Map()
export function lucideGlyph(name) {
  const key = isLucideName(name) ? name : DEFAULT_LIST_ICON
  if (!GLYPHS.has(key)) {
    const Glyph = (props) => <LucideIcon name={key} {...props} />
    GLYPHS.set(key, Glyph)
  }
  return GLYPHS.get(key)
}
